import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { provideExamdojoDateAdapter } from '@examdojo/core/date-time';
import { DateRange, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatListOption, MatSelectionList } from '@angular/material/list';
import { MatSlider, MatSliderRangeThumb } from '@angular/material/slider';
import { FormControlsOf } from '@examdojo/angular/forms';
import { connectState } from '@examdojo/angular/util';
import { FilterButtonComponent, FilterSectionDirective, MultipleFiltersButtonComponent } from '@examdojo/core/filters';
import { IconComponent } from '@examdojo/core/icon';
import { IfSmallDirective, IfWideLayoutDirective } from '@examdojo/layout';
import { StemDifficultyCategory } from '@examdojo/models/question';
import { DerivativeQuestionItemsViewStatus } from '@examdojo/question';
import { TagSelectionListComponent } from '@examdojo/tags';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { combineLatest, map, merge, Observable, of, startWith, switchMap, tap } from 'rxjs';
import { QuestionBankFiltersFormValue } from '../../app/question-bank.model';
import { QuestionBankQuery } from '../../app/question-bank.query';
import { QuestionBankService } from '../../app/question-bank.service';
import {
  createDifficultyLabelsMap$,
  createDifficultyOptions$,
  createSelectedDifficultyLabel$,
  createSelectedMaxMarksLabel$,
  createSelectedTagLabel$,
  createSelectedTypeLabel$,
  createTopicsFilterActiveCount$,
  createTypeLabelsMap$,
  createTypeOptions$,
} from '../question-bank-filter-labels.util';
import { QuestionBankTopicsExplorerComponent } from '../question-bank-topics-explorer/question-bank-topics-explorer.component';

const INIT_FORM_STATE: QuestionBankFiltersFormValue = {
  completed: false,
  difficulties: [],
  types: [],
  minPoints: 0,
  maxPoints: 0,
  minLatestScore: 0,
  maxLatestScore: 100,
  lastAttemptDateFrom: null,
  lastAttemptDateTo: null,
  tags: [],
};

@Component({
  selector: 'dojo-question-bank-filters',
  templateUrl: './question-bank-filters.component.html',
  styleUrl: './question-bank-filters.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    MatButtonToggleModule,
    MatSelectionList,
    MatListOption,
    ReactiveFormsModule,
    NgTemplateOutlet,
    TranslocoPipe,
    IconComponent,
    MatSlider,
    MatSliderRangeThumb,
    FilterButtonComponent,
    MultipleFiltersButtonComponent,
    FilterSectionDirective,
    IfSmallDirective,
    IfWideLayoutDirective,
    QuestionBankTopicsExplorerComponent,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    DatePipe,
    TagSelectionListComponent,
  ],
  providers: [provideExamdojoDateAdapter()],
})
export class QuestionBankFiltersComponent {
  constructor(
    private readonly questionBankService: QuestionBankService,
    private readonly questionBankQuery: QuestionBankQuery,
    private readonly translocoService: TranslocoService,
  ) {
    merge(this.syncFiltersFormFromStore(), this.persistFiltersFormChanges()).pipe(takeUntilDestroyed()).subscribe();
  }

  readonly form: FormGroup<FormControlsOf<QuestionBankFiltersFormValue>> = new FormGroup({
    completed: new FormControl(INIT_FORM_STATE.completed, { nonNullable: true }),
    difficulties: new FormControl<StemDifficultyCategory[]>(INIT_FORM_STATE.difficulties, { nonNullable: true }),
    types: new FormControl<DerivativeQuestionItemsViewStatus[]>(INIT_FORM_STATE.types, { nonNullable: true }),
    minPoints: new FormControl(INIT_FORM_STATE.minPoints, { nonNullable: true }),
    maxPoints: new FormControl(INIT_FORM_STATE.maxPoints, { nonNullable: true }),
    minLatestScore: new FormControl(INIT_FORM_STATE.minLatestScore, { nonNullable: true }),
    maxLatestScore: new FormControl(INIT_FORM_STATE.maxLatestScore, { nonNullable: true }),
    lastAttemptDateFrom: new FormControl<Date | null>(INIT_FORM_STATE.lastAttemptDateFrom),
    lastAttemptDateTo: new FormControl<Date | null>(INIT_FORM_STATE.lastAttemptDateTo),
    tags: new FormControl<string[]>(INIT_FORM_STATE.tags, { nonNullable: true }),
  });

  readonly difficultyOptions$ = createDifficultyOptions$();
  readonly difficultyLabelsMap$ = createDifficultyLabelsMap$(this.difficultyOptions$);
  readonly typeOptions$ = createTypeOptions$();
  readonly typeLabelsMap$ = createTypeLabelsMap$(this.typeOptions$);
  readonly selectedDifficultyLabel$ = createSelectedDifficultyLabel$(this.questionBankQuery, this.difficultyLabelsMap$);
  readonly selectedTypeLabel$ = createSelectedTypeLabel$(this.questionBankQuery, this.typeLabelsMap$);
  readonly selectedTagLabel$ = createSelectedTagLabel$(this.questionBankQuery);
  readonly selectedMaxMarksLabel$ = createSelectedMaxMarksLabel$(this.questionBankQuery, this.translocoService);

  readonly selectedLatestScoreLabel$ = this.questionBankQuery.filtersFormValue$.pipe(
    switchMap((filters) =>
      filters.maxLatestScore < 100 || filters.minLatestScore > 0
        ? this.translocoService.selectTranslate('examdojo.latest_accuracy_range', {
            min: filters.minLatestScore,
            max: filters.maxLatestScore,
          })
        : of(''),
    ),
  );

  readonly selectedLastAttemptedLabel$ = this.questionBankQuery.filtersFormValue$.pipe(
    switchMap((filters) => {
      const from = filters.lastAttemptDateFrom;
      const to = filters.lastAttemptDateTo;
      if (!from && !to) {
        return of('');
      }
      const fmt = (d: Date | null) => (d ? d.toLocaleDateString() : '');
      if (from && to) {
        return this.translocoService.selectTranslate('examdojo.last_attempted_range', { from: fmt(from), to: fmt(to) });
      }
      return this.translocoService.selectTranslate('examdojo.last_attempted_single', { date: fmt(from) || fmt(to) });
    }),
  );

  readonly filterCounts$ = combineLatest([
    this.questionBankQuery.filtersFormValue$,
    this.questionBankQuery.selectProp('marksRange'),
  ]).pipe(
    map(([filters, marksRange]) => ({
      type: filters.types.length,
      difficulty: filters.difficulties.length,
      maxMarks: filters.maxPoints < marksRange.max || filters.minPoints > marksRange.min ? 1 : 0,
      latestScore: filters.maxLatestScore < 100 || filters.minLatestScore > 0 ? 1 : 0,
      lastAttempted: filters.lastAttemptDateFrom || filters.lastAttemptDateTo ? 1 : 0,
      tags: filters.tags.length,
    })),
  );

  private readonly topicsFilterActiveCount$ = createTopicsFilterActiveCount$(this.questionBankQuery);

  readonly hasActiveFilters$ = combineLatest([this.filterCounts$, this.topicsFilterActiveCount$]).pipe(
    map(
      ([counts, topicsCount]) =>
        counts.type > 0 ||
        counts.difficulty > 0 ||
        counts.maxMarks > 0 ||
        counts.latestScore > 0 ||
        counts.lastAttempted > 0 ||
        counts.tags > 0 ||
        topicsCount > 0,
    ),
  );

  private readonly selectedDateRange$ = combineLatest([
    this.form.controls.lastAttemptDateFrom.valueChanges.pipe(startWith(this.form.controls.lastAttemptDateFrom.value)),
    this.form.controls.lastAttemptDateTo.valueChanges.pipe(startWith(this.form.controls.lastAttemptDateTo.value)),
  ]).pipe(map(([from, to]) => (from || to ? new DateRange(from, to) : null)));

  readonly state = connectState({
    difficultyOptions: this.difficultyOptions$,
    typeOptions: this.typeOptions$,
    selectedDifficultyLabel: this.selectedDifficultyLabel$,
    selectedTypeLabel: this.selectedTypeLabel$,
    selectedMaxMarksLabel: this.selectedMaxMarksLabel$,
    selectedLatestScoreLabel: this.selectedLatestScoreLabel$,
    selectedLastAttemptedLabel: this.selectedLastAttemptedLabel$,
    selectedTagLabel: this.selectedTagLabel$,
    hasActiveFilters: this.hasActiveFilters$,
    availableFilters: this.questionBankQuery.availableFilters$,
    filterCounts: this.filterCounts$,
    marksRange: this.questionBankQuery.selectProp('marksRange'),
    topicsCount: this.topicsFilterActiveCount$,
    selectedDateRange: this.selectedDateRange$,
  });

  readonly today = new Date();

  formatPercentage(value: number): string {
    return `${value}%`;
  }

  onDateRangeChange(date: Date): void {
    const currentFrom = this.form.controls.lastAttemptDateFrom.value;
    const currentTo = this.form.controls.lastAttemptDateTo.value;

    if (!currentFrom || (currentFrom && currentTo)) {
      this.form.patchValue({ lastAttemptDateFrom: date, lastAttemptDateTo: null });
    } else if (date < currentFrom) {
      this.form.patchValue({ lastAttemptDateFrom: date, lastAttemptDateTo: currentFrom });
    } else {
      this.form.patchValue({ lastAttemptDateTo: date });
    }
  }

  resetFilters(keys: Array<keyof QuestionBankFiltersFormValue>) {
    const marksRange = this.state.marksRange;
    const defaults: QuestionBankFiltersFormValue = {
      ...INIT_FORM_STATE,
      minPoints: marksRange.min,
      maxPoints: marksRange.max,
    };
    const resetValues = Object.fromEntries(keys.map((key) => [key, defaults[key]]));
    this.form.patchValue(resetValues);
  }

  sectionClearFn(keys: Array<keyof QuestionBankFiltersFormValue>): () => void {
    return () => this.resetFilters(keys);
  }

  clearAllFilters(): void {
    const marksRange = this.state.marksRange;
    this.form.reset({
      minPoints: marksRange.min,
      maxPoints: marksRange.max,
      completed: this.form.controls.completed.value,
    });
    this.questionBankService.changeFilters({ topicIds: [] });
  }

  clearTopics(): void {
    this.questionBankService.changeFilters({ topicIds: [] });
  }

  private syncFiltersFormFromStore(): Observable<unknown> {
    return this.questionBankQuery.filtersFormValue$.pipe(
      tap((filters) => {
        this.form.reset(filters, { emitEvent: false });
      }),
    );
  }

  private persistFiltersFormChanges(): Observable<unknown> {
    return this.form.valueChanges.pipe(
      tap((filters) => {
        this.questionBankService.changeFilters(filters);
      }),
    );
  }
}
